/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import dompurify from 'dompurify';
import { Navigator as BaseNavigator, UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import { ContextType, useCallback, useContext, useEffect } from 'react';
import { format, formatDistanceToNow } from 'date-fns';

import { AudienceType, DocumentVersionState, DocumentVersionType, MessageType } from './algorithm/types';
import { snackNotifications } from './notification/SnackBarUtils';
import { getMessageIndexForElement } from './Fhir/FhirSidebar_utils';
import {
  fhirHighlightForceUpdate,
  selectPredefinedWidgetsMap,
  setActiveFhirSidebarMessageIndex,
  setFhirHighlight,
} from './Fhir/fhirSlice';
import { SurveysAccepted } from './Login/userSlice';
import pearlApi from './api/pearlApi';
import { fillInEmbedPage } from './chat/components/embedpage';
import { fhirWidgetIdToRightPanelWidgetId, resolveFhirPredefinedWidgetId } from './Fhir/fhir_utils';

/**
 * These hooks re-implement the now removed useBlocker and usePrompt hooks in 'react-router-dom'.
 * Thanks for the idea @piecyk https://github.com/remix-run/react-router/issues/8139#issuecomment-953816315
 * Source: https://github.com/remix-run/react-router/commit/256cad70d3fd4500b1abcfea66f3ee622fb90874#diff-b60f1a2d4276b2a605c05e19816634111de2e8a4186fe9dd7de8e344b65ed4d3L344-L381
 */
export const topicText = 'topic' as const;
export const contextualizationText = 'contextualization' as const;
export const summaryText = 'summary' as const;

export function toTitleCase(str: string): string {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export function sort_unique(arr: string[], caseInsensitive: boolean = false): string[] {
  if (arr.length === 0) return arr;
  if (!caseInsensitive) {
    arr = [...arr].sort();
  } else {
    const collator = new Intl.Collator('en');
    arr = [...arr].sort(collator.compare);
  }

  const ret = [arr[0]!];
  for (let i = 1; i < arr.length; i++) {
    if (arr[i - 1] !== arr[i]) {
      ret.push(arr[i]!);
    }
  }
  return ret;
}

/** Sort an array by key, and make it unique */
export function sort_obj_unique<T extends keyof K, K extends Record<string, string>>(arr: K[], key: T): K[] {
  if (arr.length === 0) return arr;
  arr = arr.sort((a, b) => (a[key]! > b[key]! ? 1 : -1));
  const ret = [arr[0]!];
  for (let i = 1; i < arr.length; i++) {
    if (arr[i - 1]![key] !== arr[i]![key]!) {
      ret.push(arr[i]!);
    }
  }
  return ret;
}

export function filterObject<T>(obj: { [key: string]: T }, callback: (val: string, key: T) => boolean) {
  return Object.fromEntries(Object.entries(obj).filter(([key, val]) => callback(key, val)));
}
// From https://github.com/quilljs/quill/issues/163#issuecomment-427551866
// To detect if the quill is effectively empty - i.e. only whitespace
const re = /^\s*<p>(<[/]?(br|p)\s*( data-[^ />]*)?[/]?>|\s*)*<\/p>\s*$/;
export function isQuillContentEmpty(content: string): boolean {
  return !content?.trim() || re.test(content);
}

export const formatDateFromDate = (date: Date, returnFormat: string = 'M/d/yy') => {
  try {
    return format(date, returnFormat);
  } catch (e) {
    return '';
  }
};

export const formatDateTimeFromDate = (date: Date, returnFormat: string = 'M/d/yy, HH:mm z') => {
  try {
    return format(date, returnFormat);
  } catch (e) {
    return '';
  }
};

export const formatDate = (date?: string | null, returnFormat?: string) =>
  date ? formatDateFromDate(new Date(date), returnFormat) : '';

export const formatDateTime = (date?: string, returnFormat?: string) =>
  date ? formatDateTimeFromDate(new Date(date), returnFormat) : '';

export function formatDateInWords(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export const sortByUTCDate = (a: string | undefined, b: string | undefined) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return new Date(b).getTime() - new Date(a).getTime();
};

interface Navigator extends BaseNavigator {
  block: any;
}

type NavigationContextWithBlock = ContextType<typeof NavigationContext> & { navigator: Navigator };

/**
 * You probably don't want to use this - but usePrompt instead.
 *
 * @source https://github.com/remix-run/react-router/commit/256cad70d3fd4500b1abcfea66f3ee622fb90874
 */
function useBlocker(blocker: any, when = true) {
  const { navigator } = useContext(NavigationContext) as NavigationContextWithBlock;

  useEffect(() => {
    if (!when) {
      return;
    }
    if (!navigator) {
      throw new Error('You cannot use a blocker outside a <Router> - navigator!?');
    }

    /* NOTE - If you get 'block is not a function', it's because you upgraded react-router.
       Downgrade back to ~6.3.0:
         npm install react-router-dom@~6.3.0

       or follow the new way to do this with the history:

       https://stackoverflow.com/questions/74106591/getting-navigator-block-is-not-a-function-while-navigating-to-other-page

       */
    const unblock = navigator.block((tx: any) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          // Automatically unblock the transition so it can play all the way
          // through before retrying it. TODO: Figure out how to re-enable
          // this block if the transition is cancelled for some reason.
          unblock();
          tx.retry();
        },
      };

      blocker(autoUnblockingTx);
    });

    return unblock;
  }, [navigator, blocker, when]);
}

let prompting = false;
/**
 * Ask the user if they really want to leave this page because there are unsaved changes
 * @source https://github.com/remix-run/react-router/issues/8139#issuecomment-1021457943
 */
export function usePrompt(
  message: string | ((location: any, action: any) => string),
  when = true,
  onMovingAway?: () => Promise<void>
) {
  const blocker = useCallback(
    (tx: any) => {
      if (prompting) {
        return;
      }
      prompting = true;
      let response;
      if (typeof message === 'function') {
        response = message(tx.location, tx.action);
        if (typeof response === 'string') {
          response = window.confirm(response);
        }
      } else {
        response = window.confirm(message);
      }
      if (response) {
        if (onMovingAway) {
          onMovingAway()
            .then(() => {
              tx.retry();
              prompting = false;
            })
            .catch((e) => {
              console.error(e);
              prompting = false;
            });
        } else {
          tx.retry();
          prompting = false;
        }
      } else {
        setTimeout(() => {
          prompting = false;
        }, 100);
      }
    },
    [message, onMovingAway]
  );
  return useBlocker(blocker, when);
}

export const actionClickEvent = new Event('actionClick');

export interface CustomFindAndHighlightEventDetail {
  findText: string;
  replaceWith?: string;
  mode: 'find' | 'replaceAll' | 'replace';
  countsByEditor: { editorIndex: number; count: number; indices: number[] }[];
  laneId?: string;
  caseFlag?: 'g' | 'gi';
  replaceAsIs?: boolean;
}

export enum UniqueIdentifierForEditorIndex {
  // starting from 101 as leaving space for atmost 100 options for questionsAdd commentMore actions
  SwimlaneType = 10,
  SummaryPrefixType = 100,
  OptionType = 1000,
}

export enum CustomFindReplaceEvents {
  FIND_HIGHLIGHT_EVENT = 'custom-find-and-highlight',
  FIND_RESULT_EVENT = 'custom-find-result',
  RESET_RESULTS_EVENT = 'reset-find-and-replace',
  MOVE_TO_NEXT_OCCURRENCE_EVENT = 'move-to-next-occurrence',
  MOVE_TO_PREV_OCCURRENCE_EVENT = 'move-to-prev-occurrence',
}

declare global {
  interface Window {
    fhirHighlight: (
      event: Event,
      enter: boolean,
      inEditorMode: boolean,
      documentVersionId?: string,
      pageId?: string
    ) => void;
    actionClicked: (event: Event) => void;
    actionStopVideo: (event: Event) => void;
    fhirClick: (event: Event) => void;
  }
}

// stop any videos playing inside an action
window.actionStopVideo = (event: Event) => {
  const target = event.target as HTMLElement | null;
  if (!target || target?.nodeName !== 'ACTION') {
    return;
  }
  // Check if the action is open - we don't want to pause videos if the action is open
  if (target.classList.contains('open')) {
    return;
  }
  const videos = target.querySelectorAll('video');
  videos.forEach((v) => v.pause());
};

window.actionClicked = (event: Event) => {
  const target = event.target as HTMLElement | null;
  if (!target || target?.nodeName !== 'ACTION') {
    return;
  }
  event.stopPropagation();
  target.classList.toggle('open');
  window.actionStopVideo(event);
  window.dispatchEvent(actionClickEvent);
};

/** Called from:
 *   - RenderedHTML when mouse enters or leaves a <fhir> element
 *   - CKFhirPlugin when mouse enters or leaves a <fhir> element in the CKEditor (inEditorMode=true)
 */
window.fhirHighlight = (
  event: Event,
  enter: boolean,
  inEditorMode: boolean,
  documentVersionId?: string,
  pageId?: string
) => {
  // If we are on consumer and target has non-empty attribute "data-fhir-warning" then return
  if (!enter) {
    window.store.dispatch(setFhirHighlight(undefined));
    return;
  }
  const target = event.target as HTMLElement | null;
  if (!target || target?.nodeName !== 'FHIR') {
    return;
  }
  const messageIndex = getMessageIndexForElement(target);
  const fhirWidgetId = target.getAttribute('id');
  // Note, to get info about the fhirWidget that this fhirWidgetId points to, you need to look at the
  // particular page this element is on. That fhirWidget will point to a predefined widget.
  // This fhirWidgetId is not the predefined widget id.
  if (!fhirWidgetId) {
    return;
  }
  const hasWarning = !!target.getAttribute('data-fhir-warning');
  if (hasWarning) {
    const isCreatorMode = window.store.getState().chat.creatorMode;
    if (!isCreatorMode) {
      return;
    }
  }
  const predefinedWidgetIds = getResolvedFhirPredefinedWidgetIds(target);

  // Dispatch this event to the store. This is outside of the react lifecycle, so we can't use useAppDispatch
  window.store.dispatch(
    setFhirHighlight({
      messageIndex,
      documentVersionId,
      pageId,
      fhirWidgetIds: [fhirWidgetId],
      predefinedWidgetIds,
      hoveringOverWidget: false,
      inEditorMode,
    })
  );
  scrollFhirSidebarToPanel(target, fhirWidgetId, predefinedWidgetIds?.[0], inEditorMode);
};

function getResolvedFhirPredefinedWidgetIds(target: HTMLElement): string[] | undefined {
  const predefinedWidgetsMap = selectPredefinedWidgetsMap(window.store.getState());
  return (
    target
      .getAttribute('data-fhir-widget-id')
      ?.split(';')
      .map((id) => id.trim())
      .filter(Boolean)
      .map((id) => resolveFhirPredefinedWidgetId(id, predefinedWidgetsMap)!) || undefined
  );
}

function scrollFhirSidebarToPanel(
  target: HTMLElement,
  id: string,
  predefinedWidgetId: string | undefined,
  inEditorMode: boolean
) {
  const messageIndex = getMessageIndexForElement(target);
  const sidebarPanelId = fhirWidgetIdToRightPanelWidgetId(id, predefinedWidgetId, messageIndex, inEditorMode);
  const sidebarPanel = document.getElementById(sidebarPanelId);
  const fhirEl = document.getElementById('fhir-scroll-container');

  if (!sidebarPanel || !fhirEl) {
    return;
  }

  const containerRect = fhirEl.getBoundingClientRect();
  const elementRect = sidebarPanel.getBoundingClientRect();

  const isInView =
    elementRect.top >= containerRect.top &&
    elementRect.bottom <= containerRect.bottom &&
    elementRect.left >= containerRect.left &&
    elementRect.right <= containerRect.right;

  if (!isInView) {
    const panelOffsetTop = sidebarPanel.offsetTop;
    const centeredScrollTop = panelOffsetTop - (fhirEl.clientHeight - sidebarPanel.offsetHeight) / 2;

    fhirEl.scrollTo({
      top: Math.max(0, centeredScrollTop),
      behavior: 'smooth',
    });
  }
  // Once finished scrolling, call the highlight function again to update the arrows
  for (const i of [0, 50, 75, 100, 150, 200, 300, 400, 500, 600, 800, 1000]) {
    setTimeout(() => {
      window.store.dispatch(fhirHighlightForceUpdate());
    }, i);
  }
}

window.fhirClick = (event: Event) => {
  const target = event.target as HTMLElement | null;
  if (!target || target?.nodeName !== 'FHIR') {
    return;
  }
  const id = target.getAttribute('id');
  if (!id) {
    return;
  }
  const messageIndex = getMessageIndexForElement(target);
  window.store.dispatch(setActiveFhirSidebarMessageIndex(messageIndex));
  const predefinedWidgetId = getResolvedFhirPredefinedWidgetIds(target)?.[0];
  for (const delay of [0, 50, 150, 300, 500]) {
    setTimeout(() => {
      scrollFhirSidebarToPanel(target, id, predefinedWidgetId, false);
    }, delay);
  }
};

export function makeEditorActionsClickable(
  html: string,
  documentVersionId: string | undefined,
  pageId: string | undefined
): string;
export function makeEditorActionsClickable(
  html: string | undefined,
  documentVersionId: string | undefined,
  pageId: string | undefined
): string | undefined;
export function makeEditorActionsClickable(
  html: string | undefined,
  documentVersionId: string | undefined,
  pageId: string | undefined
) {
  return html?.replaceAll(
    '<action ',
    '<action onclick="actionClicked(event);" onmouseleave="actionStopVideo(event);" '
  );
}

/** To make the html a little bit nicer without opening up a huge security flaw,
 *  we allow iframes, but only if they are youtube or vimeo. We do this by replacing
 *  the <iframe> with a <media> element that has the src as an attribute.
 *
 *  This function reverses that, replacing the <media> element with an <iframe>,
 *  but only if the src starts with:
 *    https://www.youtube.com/embed/
 *    https://www.youtube.com/watch?v=
 *    https:\/\/vimeo.com
 *
 */
export function replaceMediaWithIframe(html: string): string {
  return html
    .replaceAll(
      /<media src="(https:\/\/www.youtube.com\/embed\/[^"]+)"[^>]*><\/media>/g,
      '<iframe src="$1" width="560" height="314" allowfullscreen="allowfullscreen" referrerpolicy="strict-origin-when-cross-origin" class="video-iframe"></iframe>'
    )
    .replaceAll(
      /<media src="(https:\/\/www.youtube.com\/watch?v=\/[^"]+)"[^>]*><\/media>/g,
      '<iframe src="$1" width="560" height="314" allowfullscreen="allowfullscreen" referrerpolicy="strict-origin-when-cross-origin" class="video-iframe"></iframe>'
    )
    .replaceAll(
      /<media src="(https:\/\/vimeo.com\/[^"]+)"[^>]*><\/media>/g,
      '<iframe src="$1" width="560" height="314" allowfullscreen="allowfullscreen" referrerpolicy="strict-origin-when-cross-origin" class="video-iframe"></iframe>'
    );
}

/** To make the html a little bit nicer without opening up a huge security flaw,
 *  we allow iframes, but only if they are youtube or vimeo. We do this by replacing
 *  the <iframe> with a <media> element that has the src as an attribute.
 *
 * e.g.
 *
 * <iframe src="https://www.youtube.com/embed/ouF-H35atOY" width="560" height="314" allowfullscreen="allowfullscreen"></iframe>
 *
 * becomes
 *
 * <media src="https://www.youtube.com/embed/ouF-H35atOY"/>
 */
export function replaceIFrameWithMedia(html: string): string {
  return html
    .replaceAll(
      /<iframe src="(https:\/\/www.youtube.com\/embed\/[^"]+)"[^>]*>\s*<\/iframe>/g,
      '<media src="$1"></media>'
    )
    .replaceAll(
      /<iframe src="(https:\/\/www.youtube.com\/watch\?v=[^"]+)"[^>]*>\s*<\/iframe>/g,
      '<media src="$1"></media>'
    )
    .replaceAll(/<iframe src="(https:\/\/vimeo.com\/[^"]+)"[^>]*>\s*<\/iframe>/g, '<media src="$1"></media>');
}

export function sanitizeHtmlAndFillInEmbedPage(
  documentVersionId: string | undefined,
  pageId: string | undefined,
  content: string,
  disableSanitizer: boolean = false
): string {
  return fillInEmbedPage(
    makeEditorActionsClickable(sanitizeHtml(content, disableSanitizer), documentVersionId, pageId)
  );
}

export function sanitizeHtml(content: string, disableSanitizer: boolean = false): string {
  return replaceMediaWithIframe(
    disableSanitizer
      ? content
      : // eslint-disable-next-line prettier/prettier
        dompurify.sanitize(replaceIFrameWithMedia(content) || '', {
          ADD_TAGS: [
            'action',
            'actioncontent',
            'fhir',
            'embedpage',
            'embeddocumentpage',
            'pearltag',
            'media',
            'comment-start',
            'comment-end',
            'qrcode',
            'suggestion-start',
            'suggestion-end',
            'use',
          ],
          ADD_ATTR: [
            'id',
            'target',
            'rel',
            'href',
            'class',
            'type',
            'src',
            'controls',
            'data-comment-end-after',
            'data-comment-start-before',
            'data-comment-start-after',
            'data-comment-end-before',
            'data-slug', // The document slug name that this link points to
            'data-doc-name', // The name of the document when this link was last updated.  So that we can automatically update the link if the document name changes
            'data-update-text', // If set to 'see-also' then the text of the link was automatically updated to 'SEE ALSO <doc name>'.  If 'without-see-also' then the text was updated to '<doc name>'. If 'without-see-also-with-page' then the text was updated to '<doc name>: <i><page name></i)'. If 'see-also-with-page' then the text was updated to 'SEE ALSO <doc name>: <i><page name></i>'.
            'data-qr-url', // The URL to generate a QR code for
            'data-fhir-widget-id', // The fhir widget id that this element points to, if it is a fhir element
            'data-fhir-widget-name', // The fhir widget name that this element points to, if it is a fhir element
            'data-fhir-warning', // The fhir warning for this element, if it is a fhir element
            'title', // for the tooltip when hovering over a fhir element, which shows the fhir widget name or the warning if there is no widget
            // For track changes suggestions
            'data-suggestion-start-before',
            'data-suggestion-start-after',
            'data-suggestion-end-before',
            'data-suggestion-end-after',
          ],
        })
  );
}

export function isEmptyObject(obj: object) {
  for (const name in obj) {
    return false;
  }
  return true;
}

// Whether a given list has any duplicate members
export function hasDuplicates(array: any[]) {
  return new Set(array).size !== array.length;
}

export function escapeHtml(unsafe: string) {
  return unsafe.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
export function unescapeHtml(safe: string) {
  return safe.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
}

export function escapeHtmlAttr(value: string) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

export function removeUndefinedProps(obj: { [key: string]: string | undefined }): { [key: string]: string } {
  for (const prop in obj) {
    if (obj.hasOwnProperty(prop) && obj[prop] === undefined) {
      delete obj[prop];
    }
  }
  // @ts-ignore
  return obj as { [key: string]: string | undefined };
}

export function reorderArray<T>(arr: T[] | undefined, oldIndex: number, newIndex: number): T[] {
  const ret = arr ? [...arr] : [];
  const moved = ret.splice(oldIndex, 1);
  if (moved[0]) {
    ret.splice(newIndex, 0, moved[0]);
  }
  return ret;
}

export function changeArrayItem<T>(arr: T[], index: number, newItem: Partial<T>): T[] {
  return [...arr.slice(0, index), { ...arr[index]!, ...newItem }, ...arr.slice(index + 1)];
}

export function removeArrayItem<T>(arr: T[], index: number): T[] {
  return [...arr.slice(0, index), ...arr.slice(index + 1)];
}

export function createRandomId(): string {
  return Math.round(Math.random() * Number.MAX_SAFE_INTEGER).toString();
}

/** Same as createRandomId, but uses 0-9A-Z */
export function createRandomAlphaNumericId(length = 9) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'; // Keep it uppercase
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

export function formatDistanceDay(date: Date): string {
  const oneDay = 1000 * 3600 * 24;
  const dateNow = new Date();
  // Count the distance from midnight today, in the LOCAL timezone
  dateNow.setHours(0, 0, 0, 0);
  const distance = date.getTime() - dateNow.getTime();
  if (distance < oneDay && distance >= 0) {
    return 'today';
  }
  if (distance > -oneDay && distance < 0) {
    return 'yesterday';
  }
  if (distance < 2 * oneDay && distance > 0) {
    return 'tomorrow';
  }
  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (e) {
    return '';
  }
}

export function hasAnyAudience(audience: DocumentVersionType['audiences']): boolean {
  return audience?.care_categories.length > 0 && audience?.patient_groups.length > 0 && audience?.personas.length > 0;
}

/** This is for generating a link to a document, which can be on multiple audiences
    We want to set an audience property to empty if:
      - The current audience property is empty, or
      - The current audience property is NOT in the list in the document audiences property
  */
export function removeFiltersFromAudience(
  audiences: DocumentVersionType['audiences'], // Audience from the document
  audience: AudienceType | undefined // Current user audience filter
): AudienceType {
  return {
    persona: audience?.persona && audiences.personas.includes(audience?.persona) ? audience?.persona : undefined,
    careCategory:
      audience?.careCategory && audiences.care_categories.includes(audience?.careCategory)
        ? audience?.careCategory
        : undefined,
    patientGroup:
      audience?.patientGroup && audiences.patient_groups.includes(audience?.patientGroup)
        ? audience?.patientGroup
        : undefined,
  };
}

export function audienceToUrlParam(
  audience: AudienceType | undefined,
  publishedOnly?: boolean,
  includeEmpty?: boolean
) {
  const paramsList: string[] = [];
  if (publishedOnly != null) {
    paramsList.push(`onlyPublished=${publishedOnly ? 'true' : 'false'}`);
  }
  if (includeEmpty || audience?.persona) {
    paramsList.push(`persona=${audience?.persona || ''}`);
  }
  if (includeEmpty || audience?.careCategory) {
    paramsList.push(`careCategory=${audience?.careCategory || ''}`);
  }
  if (includeEmpty || audience?.patientGroup) {
    paramsList.push(`patientGroup=${audience?.patientGroup || ''}`);
  }
  return paramsList.join('&');
}

export function anyAudienceMatches(
  audience: AudienceType | undefined,
  audiences: DocumentVersionType['audiences'] | undefined
) {
  if (!audience || !audiences) {
    return false;
  }
  return (
    (!audience.careCategory || audiences?.care_categories.some((x) => x === audience.careCategory)) &&
    (!audience.persona || audiences?.personas.some((x) => x === audience.persona)) &&
    (!audience.patientGroup || audiences?.patient_groups.some((x) => x === audience.patientGroup))
  );
}

export function copyToClipboard(div: HTMLDivElement | null) {
  const doc = document;
  let range;
  let selection;

  if (!div) return;

  // Find the button with id 'copyButton' and temporarily hide its content
  const copyButton = div.querySelector('#copy-button');
  let buttonText = '';

  if (copyButton) {
    buttonText = copyButton.innerHTML;
    copyButton.innerHTML = ''; // Temporarily remove button text
  }

  // Add a CSS class in case we want to change anything while copying
  div.classList.add('copying');

  if ((doc.body as any).createTextRange) {
    range = (doc.body as any).createTextRange();
    range.moveToElement(div);
    range.select();
  } else if (window.getSelection) {
    selection = window.getSelection();
    range = doc.createRange();
    range.selectNodeContents(div);

    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  document.execCommand('copy');
  window.getSelection()?.removeAllRanges();

  // Restore the button text after copying
  if (copyButton) {
    copyButton.innerHTML = buttonText;
  }

  snackNotifications.success('Copied to clipboard');
  div.classList.remove('copying');
}

const numToNiceStrDict = {
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
} as const;

export function numToNiceStr(n: number): string {
  return numToNiceStrDict[n as 2] || n.toString();
}

export const documentVersionStateToShortStrMap = {
  /** Prepublish states */
  DRAFT: 'In Definition',
  COLLABORATION: 'Collaborate',
  CONTEXTUALIZATION: 'Contextualize',
  ON_HOLD: 'On Hold',
  INTAKE: 'Intake',
  OTHER: 'Other',
  REVIEW: 'Pending Publication',
  /** Non-Prepublish states */
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived',
  SNAPSHOT: 'Snapshot',
} as const satisfies Record<keyof typeof DocumentVersionState, string>;

export const documentVersionStateToHoverStrMap = {
  /** Prepublish states */
  DRAFT: 'Topics in creation',
  COLLABORATION: 'Topics in collaboration',
  CONTEXTUALIZATION: 'Topics in contextualization',
  ON_HOLD: 'Topics on hold for future completion',
  INTAKE: 'New topics awaiting assignment',
  OTHER: 'Other',
  REVIEW: 'Topics in editing phase',
  /** Non-Prepublish states */
  PUBLISHED: 'Published topics',
  ARCHIVED: 'Archived topics including topics on hold pending reinstatement',
  SNAPSHOT: 'Snapshot',
} as const satisfies Record<keyof typeof DocumentVersionState, string>;

const documentVersionPreReviewStates = [
  DocumentVersionState.DRAFT,
  DocumentVersionState.COLLABORATION,
  DocumentVersionState.CONTEXTUALIZATION,
  DocumentVersionState.ON_HOLD,
  DocumentVersionState.INTAKE,
  DocumentVersionState.OTHER,
] as DocumentVersionState[];

const documentVersionPrepublishStates = [
  ...documentVersionPreReviewStates,
  DocumentVersionState.REVIEW,
] as DocumentVersionState[];

const documentVersionNonPreReviewStates = [
  DocumentVersionState.REVIEW,
  DocumentVersionState.PUBLISHED,
  DocumentVersionState.ARCHIVED,
  DocumentVersionState.SNAPSHOT,
] as DocumentVersionState[];

export const documentVersionNonPreReviewStatesMap = Object.fromEntries(
  documentVersionNonPreReviewStates.map((x) => [x])
);

const documentVersionAllStates = [
  ...documentVersionPreReviewStates,
  ...documentVersionNonPreReviewStates,
] as DocumentVersionState[];

/** An array like [{id: 'DRAFT', name: "In Definition"}, ...]
 *  Useful for <RectangularSelect options={documentVersionStateToShortStrArray} ... />
 */
export const documentVersionPreReviewStateToShortStrArray = documentVersionPreReviewStates.map((id) => ({
  id,
  name: documentVersionStateToShortStrMap[id],
}));

export const documentVersionAllStateToShortStrArray = documentVersionAllStates.map((id) => ({
  id,
  name: documentVersionStateToShortStrMap[id],
}));

export function isPrePublished(state: DocumentVersionState | undefined) {
  if (!state) return false;
  return documentVersionPrepublishStates.includes(state);
}

export function isPreReview(state: DocumentVersionState | undefined) {
  if (!state) return false;
  return documentVersionPreReviewStates.includes(state);
}

export const documentVersionStateToStrMap = {
  ...documentVersionStateToShortStrMap,
  ARCHIVED: 'Archived (previously published)',
} as const;

export function documentVersionStateToStr(state: DocumentVersionState | undefined) {
  return documentVersionStateToStrMap[state!] ?? state;
}

// returns a new object with the values at each key mapped using mapFn(value)
export function objectMap<T, X>(
  obj: { [key: string]: T },
  fn: (value: T, key: string, index: number) => X
): { [key: string]: X } {
  return Object.fromEntries(Object.entries(obj).map(([k, v], i) => [k, fn(v, k, i)]));
}

export const datesAreOnSameDay = (first_: string, second_: string): boolean => {
  const first = new Date(first_);
  const second = new Date(second_);
  return (
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
};

const emailRe = /\S+@\S+\.\S+/;
export function isValidEmail(email: string): boolean {
  return emailRe.test(email);
}

const npiPattern = /^(|\d{10})$/;
export function isValidNPI(npi: string) {
  // Check if the NPI is exactly 10 digits
  return npiPattern.test(npi);
}

// Apply the filter to the array, but if it will remove everything, return original array
export function bestEffortFilter<T>(arr: T[] | undefined, fn: (value: T) => boolean) {
  if (!arr) return undefined;
  const res = arr.filter(fn);
  if (res.length === 0) return arr; // return original array if this filter will remove everything
  return res;
}

export const delayMS = (t = 200) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(t);
    }, t);
  });
};

/** Return a new array with item added, but both arguments cna be undefined */
export function addToArray<T>(arr: T[] | undefined, item: T | undefined): T[] {
  if (!arr) return item ? [item] : [];
  if (!item) return arr;
  return [...arr, item];
}

export function isValidRegex(regex: string): boolean {
  try {
    new RegExp(regex);
    return true;
  } catch (e) {
    return false;
  }
}

export function getCookieData(name: string) {
  const pairs = document.cookie.split('; ');
  let count = pairs.length,
    parts;
  while (count--) {
    parts = pairs[count]!.split('=');
    if (parts[0] === name) {
      console.log('found cookie', name, parts[1]);
      return parts[1];
    }
  }
  console.log('cookie not found', name);
  return false;
}

export function deleteCookie(name: string) {
  console.log('deleting cookie', name);
  document.cookie = name + '=; Max-Age=0; path=/; domain=' + window.location.hostname;
  document.cookie = name + '=; Max-Age=0; path=/; domain=pearl-project.io';
}

/** Create a hash code from the given string */
export function hashCode(s: string | undefined): number | undefined {
  if (!s) return undefined;
  let hash = 0,
    i,
    chr;
  if (s.length === 0) return hash;
  for (i = 0; i < s.length; i++) {
    chr = s.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}
/** Generate a string with a random number of █, to make it look like blurred out text,
 * like "███ ██ █████"
 */
export function makeGrayDummyTextBlocks(numWordsMax: number = 3, numCharsMax: number = 5): string {
  const numWords = Math.floor(Math.random() * numWordsMax) + 1;
  const words = [];
  for (let i = 0; i < numWords; i++) {
    const numChars = Math.floor(Math.random() * numCharsMax) + 1;
    words.push('█'.repeat(numChars));
  }
  return words.join(' ');
}

/** If x is a value, return it.  if it's a function, call it, passing the previous value,
 *  expecting it to return the new value.
 *
 *  This is useful for when you want to set a state value, but you want to use the previous
 *  value to calculate the new value.
 *
 *  Example:
 *    const [count, setCount] = useState(0);
 *    setCount((prevCount) => prevCount + 1);
 */
export function callIfFunction<T>(state: React.SetStateAction<T>, prevState: T) {
  return state instanceof Function ? state(prevState) : state;
}

/** Delete all properties from an object */
export function clearObject(obj: { [key: string]: any }) {
  for (const prop in obj) {
    if (obj.hasOwnProperty(prop)) {
      delete obj[prop];
    }
  }
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
/**
 * Sort an array of strings in a natural way, e.g. "1", "2", "10" instead of "1", "10", "2"
 */
export function naturalSort(array: string[]) {
  return array.sort(collator.compare);
}
export function isPublishedVersion(
  documentVersion: DocumentVersionType
): documentVersion is DocumentVersionType & { state: DocumentVersionState.PUBLISHED } {
  return documentVersion.state === DocumentVersionState.PUBLISHED;
}

export function verifyMessageIsAlgorithmPage(
  message: MessageType | undefined
): asserts message is MessageType & { extra: { type: 'ALGORITHM_PAGE' } } {
  if (!message) throw new Error('Internal error - button clicked on non existance message');
  if (!message.extra) {
    // This shouldn't actually happen, but I have hundreds of unit tests that were made before this was added
    // And I'm too lazy to go through and add the extra to all of them
    message.extra = {
      type: 'ALGORITHM_PAGE',
      need_confirmation: false,
      have_confirmation: false,
      has_only_leaf_pages: true,
    };
  } else if (message.extra?.type !== 'ALGORITHM_PAGE') {
    throw new Error(`Internal error - button clicked on non algorithm message, but it is ${message.extra.type}`);
  }
}

export function downloadAsJsonFile(objectData: any, filename: string) {
  const contentType = 'application/json;charset=utf-8;';
  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
    const blob = new Blob([decodeURIComponent(encodeURI(JSON.stringify(objectData)))], { type: contentType });
    (navigator as any).msSaveOrOpenBlob(blob, filename);
  } else {
    const a = document.createElement('a');
    a.download = filename;
    a.href = 'data:' + contentType + ',' + encodeURIComponent(JSON.stringify(objectData));
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
export async function postSurveyAcceptance(survey: SurveysAccepted) {
  try {
    const data = { data: survey };
    await pearlApi.post('/prompts/accepted', data);
  } catch (err) {
    console.error('An error occured while saving survey info', err);
  }
}

/**
 * Throttles a function with an initial burst limit.
 *
 * @param func - The function to throttle.
 * @param wait - The cooldown time in milliseconds between calls after the burst limit.
 * @param burstLimit - The maximum number of calls allowed in rapid succession before throttling.
 * @returns A throttled version of the function with burst handling.
 */
export function throttleWithBurst<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  burstLimit: number
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  let burstCount = 0; // Tracks the number of calls in the current burst
  let lastReset = 0; // Timestamp of when the burst counter was last reset

  return function (this: any, ...args: Parameters<T>) {
    const now = Date.now();

    // Reset the burst counter if outside the throttle window
    if (now - lastReset > wait) {
      burstCount = 0;
      lastReset = now;
    }

    // Allow execution if within the burst limit
    if (burstCount < burstLimit) {
      burstCount++;
      return func.apply(this, args);
    }
  };
}

interface ProfessionOption {
  label: string;
  value: string;
}
export const medicalProfession: ProfessionOption[] = [
  { label: 'Nurse (RN)', value: 'Nurse (RN)' },
  { label: 'Licensed practical/vocational nurse (LPN/LVN)', value: 'Licensed practical/vocational nurse (LPN/LVN)' },
  { label: 'Pharmacist / pharmacy support team', value: 'Pharmacist / pharmacy support team' },
  { label: 'Medical assistant (MA)', value: 'Medical assistant (MA)' },
  {
    label: 'Resident / medical student (prior to obtaining an NPI)',
    value: 'Resident / medical student (prior to obtaining an NPI)',
  },
  { label: 'Other clinical practice', value: 'Other clinical practice' },
  { label: 'Non clinical', value: 'Non clinical' },
];
const adminLabelMap: Record<string, string> = {
  '/admin': '',

  '/admin/document/json': 'Create Documents From JSON',
  '/admin/searchreplace': 'Search & Replace',
  '/admin/customwidgets/json': 'Contextualization Widgets JSON',
  '/admin/widgets': 'Widgets',
  '/admin/groupedobservations': 'Grouped Observations Viewer',
  '/admin/analysis': 'Overall Document Analysis',
  '/admin/documentUpdate': 'Update All Documents',
  '/admin/consultantReviewers': 'Consultant Reviewers',
  '/admin/feedbackManagement': 'Feedback Management',

  '/admin/create-users': 'Create Trial Users',
  '/admin/org-management': 'Manage Organizations',

  '/admin/manage-users': 'Manage Users',
  '/admin/deleteuser': 'Delete User',
  '/admin/roles': 'Roles',
  '/admin/permissions': 'Your Permissions',
  '/admin/fhirDebug': 'FHIR Server Debugger',
  '/admin/azure-test-patients': 'Azure Test Patients',
  '/admin/downloadfhirpatients': 'Download Anonymous FHIR Patients',
  '/admin/logs': 'Logs',
};

export function getAdminLabel(pathname: string): string | undefined {
  if (!pathname.startsWith('/admin')) return undefined;
  return adminLabelMap[pathname] ?? 'Admin';
}

export function getFindAndReplaceCaseSensitivity(
  findTermDetail: string,
  replaceTermDetail?: string
): { findTerm: string; replaceTerm?: string; case: 'g' | 'gi'; replaceAsIs: boolean } {
  const isQuoted = (s?: string) => !!s && s.trim().startsWith('"') && s.trim().endsWith('"');
  const stripQuotes = (s: string) => s.trim().slice(1, -1).trim();

  const findQuoted = isQuoted(findTermDetail);
  const replaceQuoted = isQuoted(replaceTermDetail);

  const findTerm = findQuoted ? stripQuotes(findTermDetail) : findTermDetail.trim();
  const replaceTerm =
    replaceTermDetail === undefined
      ? undefined
      : replaceQuoted
      ? stripQuotes(replaceTermDetail)
      : replaceTermDetail.trim();

  const caseFlag: 'g' | 'gi' = replaceQuoted ? 'gi' : findQuoted ? 'g' : 'gi';
  const replaceAsIs = replaceQuoted;

  return { findTerm, replaceTerm, case: caseFlag, replaceAsIs };
}
